import React, { JSX, useEffect } from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  Navigate
} from "react-router-dom";
import { Helmet } from "react-helmet";

import Terms from "./pages/terms";
import Policy from "./pages/policy";
import Contactus from "./pages/contactus/contactus";
import Comingsoon from "./pages/coming soon";
import Maintenance from "./pages/maintanence";
import PricingPlan from "./components/pricing plan";
import Error404 from "./pages/error/Error404";
import Error500 from "./pages/error/Error500";
import Faq from "./pages/faq";
import GeneralHomeOne from "./pages/home";
import { RegisterPage } from "./pages/authentication/register";
import { LoginPage } from "./pages/authentication/login";
import ArticlesPage from "./pages/articles";
import { HomeDoctorPage } from "./pages/homeMedicare/homeDoctor";
import { HomeNursePage } from "./pages/homeMedicare/homeNurse";
import { ServicesPage } from "./pages/services";
import { ServicesDetailsPage } from "./pages/services/details";
import { OTPRegister } from "./pages/authentication/otpRegister";
import { Dashboard } from "./pages/dashboard/dashboard";
import { DashboardProfile } from "./pages/dashboard/profile";
import { DashboardNotififications } from "./pages/dashboard/notifications";
import { DashboardFamily } from "./pages/dashboard/family";
import { RegisterPhonePage } from "./pages/authentication/register-phone";
import { DashboardAppointments } from "./pages/dashboard/appointments";
import { DashboardInvoices } from "./pages/dashboard/invoices";
import { DashboardCases } from "./pages/dashboard/cases";
import { useContactType } from "./contexts/ContactContext";
import { routesMap } from "./constants/routes";
import { CheckoutPage } from "./pages/checkout";
import { CheckoutRegistrationPage } from "./pages/checkout/registration";
import { ChoosePlan } from "./pages/checkout/choosePlan";
import { AddtionalInfo } from "./pages/checkout/addtionalInfo";
import { FamilyUpdate } from "./pages/checkout/familyUpdate";
import { CheckoutPaymentPage } from "./pages/checkout/payment";
import { CheckoutThankYou } from "./pages/checkout/thankYou";
import { OTPLogin } from "./pages/authentication/otpLogin";
import { ForgotPasswordPage } from "./pages/authentication/forgotPassword";
import { ResetPasswordThankYouPage } from "./pages/authentication/ResetPasswordThankU";
import { ProcessForgotPage } from "./pages/authentication/processForgotPassword";
import { FireBaseRoutes } from "./pages/authentication/firebase";
import { PartnerLoginPage } from "./pages/authentication/partnerLogin";
import { SearchMemberPage } from "./pages/vendors/searchMember";

// ✅ Meta tags auto-injection based on current route
const MetaTags = () => {
  const { pathname } = useLocation();

  const route = Object.values(routesMap).find((r) => {
    const pathPattern = r.to.replace(/:\w+/g, "[^/]+");
    const regex = new RegExp(`^${pathPattern}$`);
    return regex.test(pathname);
  });

  if (!route) return null;

  return (
    <Helmet>
      <title>{route.title}</title>
      <meta name="description" content={route.description} />
      <meta name="keywords" content={route.keywords} />
    </Helmet>
  );
};

const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const { contact } = useContactType();

  if (!contact) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AppRouter = function () {
  const ScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
      window.scrollTo(0, 0);
    }, [pathname]);

    return null;
  };

  useEffect(() => {
    const handleMouseMove = (event: any) => {
      const cursorInner = document.querySelector(".cursor-inner") as HTMLElement;
      const cursorOuter = document.querySelector(".cursor-outer") as HTMLElement;

      if (cursorOuter) {
        cursorOuter.style.transform = `translate(${event.clientX}px, ${event.clientY}px)`;
      }

      if (cursorInner) {
        cursorInner.style.transform = `translate(${event.clientX}px, ${event.clientY}px)`;
      }
    };

    const handleMouseEnter = () => {
      const cursorInner = document.querySelector(".cursor-inner");
      const cursorOuter = document.querySelector(".cursor-outer");

      if (cursorInner) cursorInner.classList.add("s");
      if (cursorOuter) cursorOuter.classList.add("s");
    };

    const handleMouseLeave = (event: any) => {
      const cursorInner = document.querySelector(".cursor-inner");
      const cursorOuter = document.querySelector(".cursor-outer");

      if (
        event.target.tagName !== "A" ||
        !event.target.closest(".cursor-pointer")
      ) {
        if (cursorInner) cursorInner.classList.remove("cursor-hover");
        if (cursorOuter) cursorOuter.classList.remove("cursor-hover");
      }
    };

    document.body.addEventListener("mousemove", handleMouseMove);
    document.body.addEventListener("mouseenter", handleMouseEnter);
    document.body.addEventListener("mouseleave", handleMouseLeave);

    const cursorInner = document.querySelector(".cursor-inner") as HTMLElement;
    const cursorOuter = document.querySelector(".cursor-outer") as HTMLElement;

    if (cursorInner) cursorInner.style.visibility = "visible";
    if (cursorOuter) cursorOuter.style.visibility = "visible";

    return () => {
      document.body.removeEventListener("mousemove", handleMouseMove);
      document.body.removeEventListener("mouseenter", handleMouseEnter);
      document.body.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <Router basename={"/"}>
      <ScrollToTop />
      <MetaTags />
      <Routes>
        <Route path={routesMap.contactus.to} element={<Contactus />} />
        <Route path={routesMap.comingsoon.to} element={<Comingsoon />} />
        <Route path={routesMap.maintenance.to} element={<Maintenance />} />
        <Route path={routesMap.pricingPlan.to} element={<PricingPlan />} />
        <Route path={routesMap.error404.to} element={<Error404 />} />
        <Route path={routesMap.error500.to} element={<Error500 />} />
        <Route path={routesMap.faq.to} element={<Faq />} />
        <Route path={routesMap.home.to} element={<GeneralHomeOne />} />

        {/* auth pages */}
        <Route path={routesMap.register.to} element={<RegisterPage />} />
        <Route path={routesMap.registerPhone.to} element={<RegisterPhonePage />} />
        <Route path={routesMap.login.to} element={<LoginPage />} />
        <Route path={routesMap.partnerLogin.to} element={<PartnerLoginPage />} />
        <Route path={routesMap.searchMember.to} element={<SearchMemberPage />} />
        <Route path={routesMap.otpRegister.to} element={<OTPRegister />} />
        <Route path={routesMap.otpLogin.to} element={<OTPLogin />} />
        <Route path={routesMap.forgotPassword.to} element={<ForgotPasswordPage />} />
        <Route path={routesMap.forgotPasswordThankYou.to} element={<ResetPasswordThankYouPage />} />
        <Route path={routesMap.processForgotPassword.to} element={<ProcessForgotPage />} />
        <Route path={routesMap.firebase.to} element={<FireBaseRoutes />} />

        <Route path={routesMap.articles.to} element={<ArticlesPage />} />
        <Route path={routesMap.doctorToHome.to} element={<HomeDoctorPage />} />
        <Route path={routesMap.nurseToHome.to} element={<HomeNursePage />} />
        <Route path={routesMap.services.to} element={<ServicesPage />} />
        <Route path={routesMap.servicesDetails.to} element={<ServicesDetailsPage />} />

        <Route path={routesMap.checkout.to} element={<CheckoutPage />} />
        <Route path={routesMap.checkoutReg.to} element={<CheckoutRegistrationPage />} />
        <Route path={routesMap.checkoutChoosePlan.to} element={<ChoosePlan />} />
        <Route path={routesMap.checkoutAddtionalInfo.to} element={<AddtionalInfo />} />
        <Route path={routesMap.checkoutFamily.to} element={<FamilyUpdate />} />
        <Route path={routesMap.checkoutPayment.to} element={<CheckoutPaymentPage />} />
        <Route path={routesMap.checkoutThankYou.to} element={<CheckoutThankYou />} />

        {/* protected dashboard routes */}
        <Route
          path={routesMap.dashboard.to}
          element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
        />
        <Route
          path={routesMap.dashboardProfile.to}
          element={<ProtectedRoute><DashboardProfile /></ProtectedRoute>}
        />
        <Route
          path={routesMap.dashboardNotifications.to}
          element={<ProtectedRoute><DashboardNotififications /></ProtectedRoute>}
        />
        <Route
          path={routesMap.dashboardFamily.to}
          element={<ProtectedRoute><DashboardFamily /></ProtectedRoute>}
        />
        <Route
          path={routesMap.dashboardAppointments.to}
          element={<ProtectedRoute><DashboardAppointments /></ProtectedRoute>}
        />
        <Route
          path={routesMap.dashboardInvoices.to}
          element={<ProtectedRoute><DashboardInvoices /></ProtectedRoute>}
        />
        <Route
          path={routesMap.dashboardCases.to}
          element={<ProtectedRoute><DashboardCases /></ProtectedRoute>}
        />
      </Routes>
    </Router>
  );
};

export default AppRouter;
